import React, { useState, useEffect } from 'react';
import { WmsTenant, TenantBaseline, ProvisionMode, CleanupPreview, CleanupDatabaseResult, CompanyNoColumnInfo, UpdateCompanyNoResult, TenantCompanyInfo } from '../types/WmsTenant';
import { tenantApi } from '../services/api';
import { esentralApi } from '../services/esentralApi';
import { ESentralFirma, CreateESentralFirmaRequest, DryRunPreview, FirmaConfigurationSummary } from '../types/ESentral';
import { Badge } from './common/Badge';
import { Modal } from './common/Modal';
import { ESentralConfigView } from './esentral/ESentralConfigView';

interface TenantListProps {
  onEditTenant: (tenant: WmsTenant) => void;
  refreshTrigger?: number;
  onNewTenant?: () => void;
}

type SortField = 'tenantID' | 'tenantName' | 'databaseName' | 'serverName' | 'status' | 'contactPerson' | 'createdDate' | 'contactEmail' | 'eSentralFirmaNr';
type SortDirection = 'asc' | 'desc';

const TenantList: React.FC<TenantListProps> = ({ onEditTenant, refreshTrigger }) => {
  const [tenants, setTenants] = useState<WmsTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTenant, setEditingTenant] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>('tenantID');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [editFormData, setEditFormData] = useState<WmsTenant>({} as WmsTenant);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [provisioningTenant, setProvisioningTenant] = useState<number | null>(null);
  const [databaseStatuses, setDatabaseStatuses] = useState<{[key: number]: boolean}>({});
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateDatabases, setTemplateDatabases] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pendingProvisionTenantId, setPendingProvisionTenantId] = useState<number | null>(null);
  const [provisionMode, setProvisionMode] = useState<ProvisionMode>('baseline');
  const [activeBaseline, setActiveBaseline] = useState<TenantBaseline | null>(null);
  const [provisionNotice, setProvisionNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [newTenantData, setNewTenantData] = useState<WmsTenant>({
    tenantName: '',
    databaseName: undefined, // Will be set during provisioning
    serverName: undefined,
    status: 'Active',
    maxUsers: 10,
    storageQuotaGB: 100,
  });

  // eSentral state
  const [esentralFirmaList, setEsentralFirmaList] = useState<ESentralFirma[]>([]);
  const [showEsentralModal, setShowEsentralModal] = useState(false);
  const [creatingEsentralForTenant, setCreatingEsentralForTenant] = useState<WmsTenant | null>(null);
  const [esentralFormData, setEsentralFormData] = useState<CreateESentralFirmaRequest>({
    kundeNr: '',
    navn: '',
    sourceFirmaNr: '001',
    vismaNetCompanyNo: '',
    vismaNetTenantId: '',
    businessNxtCompanyNo: '',
    updateTenantDatabaseCompanyNo: false,
  });
  const [esentralCreating, setEsentralCreating] = useState(false);
  const [esentralError, setEsentralError] = useState<string | null>(null);
  const [esentralPreview, setEsentralPreview] = useState<DryRunPreview | null>(null);
  const [esentralPreviewing, setEsentralPreviewing] = useState(false);

  // eSentral configuration view state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewingConfig, setViewingConfig] = useState<FirmaConfigurationSummary | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Database cleanup state
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupTenant, setCleanupTenant] = useState<WmsTenant | null>(null);
  const [cleanupPreview, setCleanupPreview] = useState<CleanupPreview | null>(null);
  const [cleanupResult, setCleanupResult] = useState<CleanupDatabaseResult | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);

  // CompanyNo update state
  const [showCompanyNoModal, setShowCompanyNoModal] = useState(false);
  const [companyNoTenant, setCompanyNoTenant] = useState<WmsTenant | null>(null);
  const [companyNoColumns, setCompanyNoColumns] = useState<CompanyNoColumnInfo[]>([]);
  const [companyNoLoading, setCompanyNoLoading] = useState(false);
  const [companyNoUpdating, setCompanyNoUpdating] = useState(false);
  const [companyNoResult, setCompanyNoResult] = useState<UpdateCompanyNoResult | null>(null);
  const [newCompanyNo, setNewCompanyNo] = useState('');
  const [companyNoCompanies, setCompanyNoCompanies] = useState<TenantCompanyInfo[]>([]);
  const [oldCompanyNo, setOldCompanyNo] = useState('');

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantApi.getAllTenants();
      setTenants(data);
      
      // Check database status for all tenants
      const statusPromises = data.map(async (tenant) => {
        if (tenant.tenantID) {
          try {
            const status = await tenantApi.getDatabaseStatus(tenant.tenantID);
            return { tenantId: tenant.tenantID, exists: status.databaseExists };
          } catch {
            return { tenantId: tenant.tenantID, exists: false };
          }
        }
        return null;
      });
      
      const statuses = await Promise.all(statusPromises);
      const statusMap: {[key: number]: boolean} = {};
      
      statuses.forEach(status => {
        if (status) {
          statusMap[status.tenantId] = status.exists;
        }
      });
      
      setDatabaseStatuses(statusMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Feil ved henting av tenanter');
    } finally {
      setLoading(false);
    }
  };

  const fetchEsentralFirmaList = async () => {
    try {
      const firma = await esentralApi.getAllFirma();
      setEsentralFirmaList(firma);
    } catch (err) {
      console.error('Error fetching eSentral firma:', err);
      // Non-critical, don't show error to user
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchEsentralFirmaList();
  }, [refreshTrigger]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Er du sikker på at du vil slette denne tenanten?')) {
      try {
        await tenantApi.deleteTenant(id);
        await fetchTenants(); // Refresh the list
      } catch (err) {
        console.error('Error deleting tenant:', err);
        setError('Feil ved sletting av tenant');
      }
    }
  };

  const handleProvisionDatabase = async (tenantId: number) => {
    // Hent aktiv baseline og mal-databaser (for «Klon kunde») parallelt
    setPendingProvisionTenantId(tenantId);
    setProvisionNotice(null);
    setLoadingTemplates(true);

    try {
      const [baseline, templates] = await Promise.all([
        tenantApi.getActiveBaseline(),
        tenantApi.getTemplateDatabases(),
      ]);
      setActiveBaseline(baseline);
      setProvisionMode(baseline ? 'baseline' : 'clone');
      setTemplateDatabases(templates);
      setSelectedTemplate(templates[0] || '');
      setShowDatabaseModal(true);
    } catch (err) {
      console.error('Error loading provisioning options:', err);
      setProvisionNotice({ type: 'error', text: 'Kunne ikke hente baseline/mal-databaser' });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleConfirmProvision = async () => {
    if (!pendingProvisionTenantId) return;
    if (provisionMode === 'clone' && !selectedTemplate) return;

    setProvisioningTenant(pendingProvisionTenantId);
    setShowDatabaseModal(false);

    try {
      const result = await tenantApi.provisionDatabase(pendingProvisionTenantId, provisionMode, selectedTemplate);
      setProvisionNotice({ type: 'success', text: `${result.databaseName}: ${result.message}` });
      await fetchTenants(); // Refresh to show updated database info
    } catch (err: any) {
      console.error('Error during database provisioning:', err);
      const serverError = err?.response?.data?.error ?? err?.response?.data;
      setProvisionNotice({
        type: 'error',
        text: typeof serverError === 'string' && serverError ? serverError : 'Feil under opprettelse av database',
      });
    } finally {
      setProvisioningTenant(null);
      setPendingProvisionTenantId(null);
      setSelectedTemplate('');
    }
  };

  const handleCancelProvision = () => {
    setShowDatabaseModal(false);
    setPendingProvisionTenantId(null);
    setSelectedTemplate('');
  };

  const handleEditStart = (tenant: WmsTenant) => {
    setEditingTenant(tenant.tenantID!);
    setEditFormData({ ...tenant });
  };

  const handleEditCancel = () => {
    setEditingTenant(null);
    setEditFormData({} as WmsTenant);
  };

  const handleEditSave = async () => {
    if (!editingTenant) return;
    
    try {
      await tenantApi.updateTenant(editingTenant, editFormData);
      setEditingTenant(null);
      setEditFormData({} as WmsTenant);
      await fetchTenants(); // Refresh the list
    } catch (err) {
      console.error('Error updating tenant:', err);
      setError('Feil ved oppdatering av tenant');
    }
  };

  const handleFieldChange = (field: keyof WmsTenant, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNewTenantStart = () => {
    setIsCreatingNew(true);
    setEditingTenant(null);
  };

  const handleNewTenantCancel = () => {
    setIsCreatingNew(false);
    setNewTenantData({
      tenantName: '',
      databaseName: '',
      serverName: 'IPBDBV006\\IPB',
      status: 'Active',
      maxUsers: 10,
      storageQuotaGB: 100,
    } as WmsTenant);
  };

  const handleNewTenantSave = async () => {
    try {
      await tenantApi.createTenant(newTenantData);
      setIsCreatingNew(false);
      setNewTenantData({
        tenantName: '',
        databaseName: undefined,
        serverName: undefined,
        status: 'Active',
        maxUsers: 10,
        storageQuotaGB: 100,
      });
      await fetchTenants(); // Refresh the list
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError('Feil ved opprettelse av ny tenant');
    }
  };

  const handleNewTenantFieldChange = (field: keyof WmsTenant, value: any) => {
    setNewTenantData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // eSentral handlers
  const getEsentralStatus = (tenant: WmsTenant): 'configured' | 'not_configured' => {
    if (!tenant.eSentralFirmaNr) return 'not_configured';
    const found = esentralFirmaList.find(f => f.firmaNr === tenant.eSentralFirmaNr);
    return found ? 'configured' : 'not_configured';
  };

  const handleCreateEsentralStart = (tenant: WmsTenant) => {
    setCreatingEsentralForTenant(tenant);
    setEsentralFormData({
      kundeNr: tenant.tenantID?.toString() || '',
      navn: tenant.tenantName,
      sourceFirmaNr: '001',
      tenantId: tenant.tenantID,
      vismaNetCompanyNo: '',
      vismaNetTenantId: '',
      businessNxtCompanyNo: '',
      updateTenantDatabaseCompanyNo: false,
    });
    setEsentralError(null);
    setShowEsentralModal(true);
  };

  const handleCreateEsentralCancel = () => {
    setShowEsentralModal(false);
    setCreatingEsentralForTenant(null);
    setEsentralFormData({
      kundeNr: '',
      navn: '',
      sourceFirmaNr: '001',
      vismaNetCompanyNo: '',
      vismaNetTenantId: '',
      businessNxtCompanyNo: '',
      updateTenantDatabaseCompanyNo: false,
    });
    setEsentralError(null);
    setEsentralPreview(null);
  };

  const handleCreateEsentralPreview = async () => {
    if (!creatingEsentralForTenant || !creatingEsentralForTenant.tenantID) return;

    setEsentralPreviewing(true);
    setEsentralError(null);
    setEsentralPreview(null);

    try {
      const result = await esentralApi.createFirmaForTenant(
        creatingEsentralForTenant.tenantID,
        { ...esentralFormData, dryRun: true }
      );

      if (result.success && result.preview) {
        setEsentralPreview(result.preview);
      } else {
        setEsentralError(result.error || result.message || 'Kunne ikke generere forhåndsvisning');
      }
    } catch (err: any) {
      console.error('Error previewing eSentral firma:', err);
      setEsentralError(err.response?.data?.message || err.message || 'Nettverksfeil');
    } finally {
      setEsentralPreviewing(false);
    }
  };

  const handleCreateEsentralConfirm = async () => {
    if (!creatingEsentralForTenant || !creatingEsentralForTenant.tenantID) return;

    setEsentralCreating(true);
    setEsentralError(null);

    try {
      const result = await esentralApi.createFirmaForTenant(
        creatingEsentralForTenant.tenantID,
        esentralFormData
      );

      if (result.success) {
        // Refresh both tenant list and eSentral firma list
        await Promise.all([fetchTenants(), fetchEsentralFirmaList()]);
        handleCreateEsentralCancel();
      } else {
        setEsentralError(result.error || result.message || 'Ukjent feil ved opprettelse');
      }
    } catch (err: any) {
      console.error('Error creating eSentral firma:', err);
      setEsentralError(err.response?.data?.message || err.message || 'Nettverksfeil ved opprettelse');
    } finally {
      setEsentralCreating(false);
    }
  };

  const handleViewConfiguration = async (tenantId: number) => {
    setLoadingConfig(true);
    setShowConfigModal(true);
    setViewingConfig(null);
    setConfigError(null);

    try {
      const config = await esentralApi.getTenantConfiguration(tenantId);
      setViewingConfig(config);
    } catch (err: any) {
      console.error('Error fetching eSentral configuration:', err);
      setViewingConfig(null);
      setConfigError(typeof err.response?.data === 'string' ? err.response.data : null);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setViewingConfig(null);
  };

  // Database cleanup handlers
  const handleOpenCleanupModal = async (tenant: WmsTenant) => {
    if (!tenant.tenantID) return;

    setCleanupTenant(tenant);
    setCleanupPreview(null);
    setCleanupResult(null);
    setShowCleanupModal(true);
    setCleanupLoading(true);

    try {
      const preview = await tenantApi.getCleanupPreview(tenant.tenantID);
      setCleanupPreview(preview);
    } catch (err: any) {
      console.error('Error fetching cleanup preview:', err);
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCloseCleanupModal = () => {
    setShowCleanupModal(false);
    setCleanupTenant(null);
    setCleanupPreview(null);
    setCleanupResult(null);
  };

  const handleExecuteCleanup = async () => {
    if (!cleanupTenant?.tenantID) return;

    setCleanupExecuting(true);
    setCleanupResult(null);

    try {
      const result = await tenantApi.cleanupDatabase(cleanupTenant.tenantID, {
        dryRun: false
      });
      setCleanupResult(result);

      if (result.success) {
        // Refresh preview to show updated row counts
        const preview = await tenantApi.getCleanupPreview(cleanupTenant.tenantID);
        setCleanupPreview(preview);
      }
    } catch (err: any) {
      console.error('Error executing cleanup:', err);
      setCleanupResult({
        success: false,
        message: err.response?.data?.error || err.message || 'Ukjent feil',
        tableResults: [],
        totalRowsDeleted: 0,
        tablesProcessed: 0,
        tablesFailed: 0,
        duration: ''
      });
    } finally {
      setCleanupExecuting(false);
    }
  };

  // CompanyNo update handlers
  const handleOpenCompanyNoModal = async (tenant: WmsTenant) => {
    if (!tenant.tenantID) return;

    setCompanyNoTenant(tenant);
    setCompanyNoColumns([]);
    setCompanyNoResult(null);
    setNewCompanyNo(tenant.eSentralFirmaNr || '');
    setCompanyNoCompanies([]);
    setOldCompanyNo('');
    setShowCompanyNoModal(true);
    setCompanyNoLoading(true);

    try {
      const [columns, companies] = await Promise.all([
        tenantApi.getCompanyNoColumns(tenant.tenantID),
        tenantApi.getCompanies(tenant.tenantID)
      ]);
      setCompanyNoColumns(columns);
      setCompanyNoCompanies(companies);
      // Én rad i Company → den er gammel CompanyNo; flere → brukeren må velge
      setOldCompanyNo(companies.length === 1 ? companies[0].companyNo : '');
    } catch (err: any) {
      console.error('Error fetching CompanyNo columns:', err);
    } finally {
      setCompanyNoLoading(false);
    }
  };

  const handleCloseCompanyNoModal = () => {
    setShowCompanyNoModal(false);
    setCompanyNoTenant(null);
    setCompanyNoColumns([]);
    setCompanyNoResult(null);
    setNewCompanyNo('');
    setCompanyNoCompanies([]);
    setOldCompanyNo('');
  };

  const handleExecuteCompanyNoUpdate = async () => {
    if (!companyNoTenant?.tenantID || !newCompanyNo.trim() || !oldCompanyNo) return;

    setCompanyNoUpdating(true);
    setCompanyNoResult(null);

    try {
      const result = await tenantApi.updateCompanyNo(companyNoTenant.tenantID, newCompanyNo.trim(), oldCompanyNo);
      setCompanyNoResult(result);

      if (result.success) {
        // Refresh columns to show updated values
        const [columns, companies] = await Promise.all([
          tenantApi.getCompanyNoColumns(companyNoTenant.tenantID),
          tenantApi.getCompanies(companyNoTenant.tenantID)
        ]);
        setCompanyNoColumns(columns);
        setCompanyNoCompanies(companies);
      }
    } catch (err: any) {
      console.error('Error updating CompanyNo:', err);
      setCompanyNoResult({
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || err.message || 'Ukjent feil',
        tableResults: [],
        totalRowsUpdated: 0
      });
    } finally {
      setCompanyNoUpdating(false);
    }
  };

  // Setup script download handler
  const handleDownloadSetupScript = async (tenant: WmsTenant) => {
    if (!tenant.tenantID) return;

    try {
      await tenantApi.downloadSetupScript(tenant.tenantID);
    } catch (err: any) {
      console.error('Error downloading setup script:', err);
      alert(err.response?.data?.error || 'Kunne ikke laste ned setup-script');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nb-NO');
  };

  // Sortering
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Håndter null/undefined verdier
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Håndter datoer
    if (sortField === 'createdDate') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Håndter tall
    if (sortField === 'tenantID') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    }

    // Streng-sammenligning (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Laster tenanter...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Noe gikk galt</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-4 w-full">
      {provisionNotice && (
        <div className={`flex items-start justify-between gap-4 rounded-md border p-3 text-sm ${
          provisionNotice.type === 'success'
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-red-200 bg-red-50 text-red-800'
        }`}>
          <span>{provisionNotice.text}</span>
          <button onClick={() => setProvisionNotice(null)} className="shrink-0 font-medium hover:underline">
            Lukk
          </button>
        </div>
      )}

      {/* Table Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Tenanter ({tenants.length})</h2>
        <button
          onClick={handleNewTenantStart}
          disabled={isCreatingNew}
          className="inline-flex items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ny Tenant
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 w-full">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('tenantID')}
              >
                <div className="flex items-center">
                  ID
                  <SortIcon field="tenantID" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('tenantName')}
              >
                <div className="flex items-center">
                  Tenant Navn
                  <SortIcon field="tenantName" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('databaseName')}
              >
                <div className="flex items-center">
                  Database
                  <SortIcon field="databaseName" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('serverName')}
              >
                <div className="flex items-center">
                  Server
                  <SortIcon field="serverName" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contactPerson')}
              >
                <div className="flex items-center">
                  Kontaktperson
                  <SortIcon field="contactPerson" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('createdDate')}
              >
                <div className="flex items-center">
                  Opprettet
                  <SortIcon field="createdDate" />
                </div>
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('contactEmail')}
              >
                <div className="flex items-center">
                  E-post
                  <SortIcon field="contactEmail" />
                </div>
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notater
              </th>
              <th
                className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('eSentralFirmaNr')}
              >
                <div className="flex items-center">
                  eSentral FirmaNr
                  <SortIcon field="eSentralFirmaNr" />
                </div>
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Database Status
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                eSentral Status
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* New Tenant Row */}
            {isCreatingNew && (
              <tr className="bg-green-50 border-2 border-green-200">
                <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                  <span className="text-green-600 font-medium">NY</span>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <input
                    type="text"
                    value={newTenantData.tenantName || ''}
                    onChange={(e) => handleNewTenantFieldChange('tenantName', e.target.value)}
                    placeholder="Tenant navn"
                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                  <em>Vil bli opprettet senere</em>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                  <em>Settes automatisk</em>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <select
                    value={newTenantData.status || 'Active'}
                    onChange={(e) => handleNewTenantFieldChange('status', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <input
                    type="text"
                    value={newTenantData.contactPerson || ''}
                    onChange={(e) => handleNewTenantFieldChange('contactPerson', e.target.value)}
                    placeholder="Kontaktperson"
                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                  -
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <input
                    type="email"
                    value={newTenantData.contactEmail || ''}
                    onChange={(e) => handleNewTenantFieldChange('contactEmail', e.target.value)}
                    placeholder="E-post"
                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <textarea
                    value={newTenantData.notes || ''}
                    onChange={(e) => handleNewTenantFieldChange('notes', e.target.value)}
                    placeholder="Notater"
                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                    rows={1}
                  />
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                  -
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    Venter på opprettelse
                  </span>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    -
                  </span>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleNewTenantSave}
                      className="text-green-600 hover:text-green-800 transition-colors"
                      title="Lagre ny tenant"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={handleNewTenantCancel}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                      title="Avbryt"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            )}
            
            {sortedTenants.map((tenant) => {
              const isEditing = editingTenant === tenant.tenantID;
              
              return (
                <tr key={tenant.tenantID} className={`transition-colors ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                    {tenant.tenantID}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.tenantName || ''}
                        onChange={(e) => handleFieldChange('tenantName', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-xs font-medium text-gray-900">{tenant.tenantName}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.databaseName || ''}
                        onChange={(e) => handleFieldChange('databaseName', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">
                        {tenant.databaseName}
                      </code>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.serverName || ''}
                        onChange={(e) => handleFieldChange('serverName', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-xs text-gray-500">{tenant.serverName}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <select
                        value={editFormData.status || ''}
                        onChange={(e) => handleFieldChange('status', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Suspended">Suspended</option>
                      </select>
                    ) : (
                      <Badge variant={tenant.status === 'Active' ? 'success' : 'danger'}>
                        {tenant.status}
                      </Badge>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.contactPerson || ''}
                        onChange={(e) => handleFieldChange('contactPerson', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-xs text-gray-900">{tenant.contactPerson || '-'}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                    {formatDate(tenant.createdDate)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="email"
                        value={editFormData.contactEmail || ''}
                        onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-xs text-gray-900">{tenant.contactEmail || '-'}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {isEditing ? (
                      <textarea
                        value={editFormData.notes || ''}
                        onChange={(e) => handleFieldChange('notes', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={1}
                      />
                    ) : (
                      <div className="text-xs text-gray-900 max-w-32 truncate" title={tenant.notes}>
                        {tenant.notes || '-'}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.eSentralFirmaNr || ''}
                        onChange={(e) => handleFieldChange('eSentralFirmaNr', e.target.value)}
                        className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="FirmaNr"
                      />
                    ) : (
                      <div className="text-xs text-gray-500">{tenant.eSentralFirmaNr || '-'}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                    <div className="flex items-center space-x-2">
                      {databaseStatuses[tenant.tenantID!] ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✅ Opprettet
                        </span>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            📋 Planlagt
                          </span>
                          <button
                            onClick={() => handleProvisionDatabase(tenant.tenantID!)}
                            disabled={provisioningTenant === tenant.tenantID}
                            className="text-xs bg-green-500 hover:bg-green-600 text-white font-medium px-2 py-0.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Opprett fysisk database"
                          >
                            {provisioningTenant === tenant.tenantID ? 'Oppretter...' : 'Opprett DB'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                    <div className="flex items-center space-x-2">
                      {getEsentralStatus(tenant) === 'configured' ? (
                        <>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Konfigurert ({tenant.eSentralFirmaNr})
                          </span>
                          <button
                            onClick={() => handleViewConfiguration(tenant.tenantID!)}
                            className="text-xs bg-blue-500 hover:bg-blue-600 text-white font-medium px-2 py-0.5 rounded"
                            title="Vis eSentral konfigurasjon"
                          >
                            Vis konfig
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Ikke konfigurert
                          </span>
                          <button
                            onClick={() => handleCreateEsentralStart(tenant)}
                            className="text-xs bg-purple-500 hover:bg-purple-600 text-white font-medium px-2 py-0.5 rounded"
                            title="Opprett eSentral firma"
                          >
                            Opprett
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium">
                    <div className="flex items-center space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Lagre"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title="Avbryt"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditStart(tenant)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Rediger"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(tenant.tenantID!)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Slett"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          {/* Cleanup button - only show if database exists */}
                          {databaseStatuses[tenant.tenantID!] && (
                            <button
                              onClick={() => handleOpenCleanupModal(tenant)}
                              className="text-orange-600 hover:text-orange-800 transition-colors"
                              title="Tøm database"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                          )}
                          {/* CompanyNo update button - only show if database exists */}
                          {databaseStatuses[tenant.tenantID!] && (
                            <button
                              onClick={() => handleOpenCompanyNoModal(tenant)}
                              className="text-purple-600 hover:text-purple-800 transition-colors"
                              title="Oppdater CompanyNo"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </button>
                          )}
                          {/* Setup script download - only show if database AND eSentral FirmaNr exists */}
                          {databaseStatuses[tenant.tenantID!] && tenant.eSentralFirmaNr && (
                            <button
                              onClick={() => handleDownloadSetupScript(tenant)}
                              className="text-teal-600 hover:text-teal-800 transition-colors"
                              title="Last ned setup-script"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                          )}
                          {/* Roter storskjerm-nøkkel — adskilt med skille for å unngå feilklikk */}
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Rotere storskjerm-nøkkelen for ${tenant.tenantName}?\n\nDen gamle lenken slutter å virke umiddelbart. WMS-løsningen henter ny lenke automatisk.`)) return;
                              try {
                                await tenantApi.rotateDashboardKey(tenant.tenantID!);
                                alert(`Storskjerm-nøkkelen for ${tenant.tenantName} er rotert.`);
                              } catch (e: any) {
                                const status = e?.response?.status;
                                alert(`Kunne ikke rotere storskjerm-nøkkelen${status ? ` (HTTP ${status})` : ' (ingen kontakt med API-et)'}`);
                              }
                            }}
                            className="text-amber-600 hover:text-amber-800 transition-colors ml-3 pl-3 border-l border-gray-300"
                            title="Roter storskjerm-nøkkel (gammel lenke slutter å virke)"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {tenants.length === 0 && (
        <div className="text-center py-12">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-gray-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen tenanter funnet</h3>
          <p className="text-gray-500">Klikk på "Ny Tenant" for å legge til din første tenant.</p>
        </div>
      )}
    </div>

    {/* Database Template Selection Modal */}
    <Modal
      isOpen={showDatabaseModal}
      onClose={handleCancelProvision}
      title="Opprett database"
    >
      <div className="space-y-4">
        <label className={`flex gap-3 rounded-md border p-3 ${activeBaseline ? 'cursor-pointer' : 'opacity-60'} ${
          provisionMode === 'baseline' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}>
          <input
            type="radio"
            name="provisionMode"
            className="mt-1"
            checked={provisionMode === 'baseline'}
            disabled={!activeBaseline}
            onChange={() => setProvisionMode('baseline')}
          />
          <div className="text-sm">
            <div className="font-medium text-gray-900">Ny tom database (anbefalt)</div>
            {activeBaseline ? (
              <p className="text-gray-600">
                Fra fasit (oppdatert {new Date(activeBaseline.createdOn).toLocaleDateString('nb-NO')}): kun tabeller
                i bruk, ingen data. Firma 001, Hovedlager og to faste brukere (Operatør og Lagersjef) opprettes.
                Migrasjoner som er nyere enn fasiten, kjøres etterpå.
              </p>
            ) : (
              <p className="text-gray-600">Ingen fasit for nye tenant-baser i dette miljøet.</p>
            )}
          </div>
        </label>

        <label className={`flex gap-3 rounded-md border p-3 cursor-pointer ${
          provisionMode === 'clone' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}>
          <input
            type="radio"
            name="provisionMode"
            className="mt-1"
            checked={provisionMode === 'clone'}
            onChange={() => setProvisionMode('clone')}
          />
          <div className="text-sm w-full space-y-2">
            <div>
              <div className="font-medium text-gray-900">Klon kunde</div>
              <p className="text-gray-600">
                Full kopi (backup/restore) av en eksisterende tenant, inkludert data og legacy-tabeller.
                CustomerNo i Profile oppdateres.
              </p>
            </div>
            {provisionMode === 'clone' && (
              loadingTemplates ? (
                <p className="text-gray-500">Laster tilgjengelige databaser...</p>
              ) : templateDatabases.length > 0 ? (
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {templateDatabases.map(db => (
                    <option key={db} value={db}>{db}</option>
                  ))}
                </select>
              ) : (
                <p className="text-red-500">Ingen databaser å klone fra</p>
              )
            )}
          </div>
        </label>

        <div className="flex justify-end space-x-2 pt-2">
          <button
            onClick={handleCancelProvision}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirmProvision}
            disabled={loadingTemplates || (provisionMode === 'clone' ? !selectedTemplate : !activeBaseline)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            Opprett Database
          </button>
        </div>
      </div>
    </Modal>

    {/* eSentral Create Modal */}
    <Modal
      isOpen={showEsentralModal}
      onClose={handleCreateEsentralCancel}
      title="Opprett eSentral Firma"
    >
      <div className="space-y-4">
        {creatingEsentralForTenant && (
          <div className="bg-blue-50 p-3 rounded">
            <p className="text-sm text-blue-800">
              <strong>Tenant:</strong> {creatingEsentralForTenant.tenantName} (ID: {creatingEsentralForTenant.tenantID})
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kundenummer
          </label>
          <input
            type="text"
            value={esentralFormData.kundeNr}
            onChange={(e) => setEsentralFormData(prev => ({ ...prev, kundeNr: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Kundenummer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Firmanavn
          </label>
          <input
            type="text"
            value={esentralFormData.navn}
            onChange={(e) => setEsentralFormData(prev => ({ ...prev, navn: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Firmanavn"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kilde-firma (kopieres fra)
          </label>
          <input
            type="text"
            value={esentralFormData.sourceFirmaNr || '001'}
            onChange={(e) => setEsentralFormData(prev => ({ ...prev, sourceFirmaNr: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="001"
          />
          <p className="text-xs text-gray-500 mt-1">Standard kilde-firma er 001</p>
        </div>

        {/* ERP Integrasjoner */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">ERP-integrasjon (valgfritt)</h4>

          {/* Visma.net */}
          <div className="bg-gray-50 p-3 rounded mb-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Visma.net</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">CompanyNo</label>
                <input
                  type="text"
                  value={esentralFormData.vismaNetCompanyNo || ''}
                  onChange={(e) => setEsentralFormData(prev => ({ ...prev, vismaNetCompanyNo: e.target.value }))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="CompanyNo"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">TenantID</label>
                <input
                  type="text"
                  value={esentralFormData.vismaNetTenantId || ''}
                  onChange={(e) => setEsentralFormData(prev => ({ ...prev, vismaNetTenantId: e.target.value }))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="TenantID"
                />
              </div>
            </div>
          </div>

          {/* BusinessNXT */}
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs font-medium text-gray-700 mb-2">BusinessNXT 001</p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">CompanyNo</label>
              <input
                type="text"
                value={esentralFormData.businessNxtCompanyNo || ''}
                onChange={(e) => setEsentralFormData(prev => ({ ...prev, businessNxtCompanyNo: e.target.value }))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                placeholder="CompanyNo"
              />
            </div>
          </div>

          {/* Advarsel hvis ingen ERP er fylt ut */}
          {!esentralFormData.vismaNetCompanyNo && !esentralFormData.businessNxtCompanyNo && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-3">
              <p className="text-xs text-yellow-800">
                <strong>Advarsel:</strong> Ingen ERP-integrasjon er spesifisert.
                Du kan fortsette, men DBTilkobling for Visma.net/BusinessNXT vil ikke bli konfigurert.
              </p>
            </div>
          )}

          {/* Oppdater CompanyNo i tenant-database */}
          <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={esentralFormData.updateTenantDatabaseCompanyNo || false}
                onChange={(e) => setEsentralFormData(prev => ({ ...prev, updateTenantDatabaseCompanyNo: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Oppdater CompanyNo i tenant-database</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Oppdaterer alle CompanyNo/CustomerNo-kolonner i WMS tenant-databasen med det nye eSentral FirmaNr.
                  eSentral blir master for CompanyNo.
                </p>
              </div>
            </label>
          </div>
        </div>

        {esentralError && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-800">{esentralError}</p>
          </div>
        )}

        {/* Forhåndsvisning av endringer */}
        {esentralPreview && (
          <div className="border border-blue-200 rounded-md overflow-hidden">
            <div className="bg-blue-100 px-3 py-2">
              <h4 className="text-sm font-medium text-blue-900">
                Forhåndsvisning - Firma {esentralPreview.nextFirmaNr} (fra {esentralPreview.sourceFirmaNr})
              </h4>
            </div>
            <div className="bg-white p-3 max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1 pr-2 font-medium text-gray-600">Tabell</th>
                    <th className="text-left py-1 pr-2 font-medium text-gray-600">Endring</th>
                    <th className="text-left py-1 font-medium text-gray-600">Verdi</th>
                  </tr>
                </thead>
                <tbody>
                  {esentralPreview.changes.map((change, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-1.5 pr-2 align-top">
                        <code className="bg-gray-100 px-1 rounded text-xs">{change.table}</code>
                        {change.affectedRows !== undefined && change.affectedRows > 0 && (
                          <span className="ml-1 text-gray-400">({change.affectedRows} rader)</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 align-top text-gray-700">{change.description}</td>
                      <td className="py-1.5 align-top">
                        {change.oldValue && (
                          <div className="text-red-600 line-through text-xs">{change.oldValue}</div>
                        )}
                        {change.newValue && (
                          <div className="text-green-700 text-xs break-all">{change.newValue}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-purple-50 p-3 rounded">
          <p className="text-sm text-purple-800">
            <strong>Info:</strong> Det vil bli opprettet et nytt firma i eSentral basert på konfigurasjonen
            fra kilde-firma. Firmanummeret vil automatisk lagres på tenant.
          </p>
        </div>

        <div className="flex justify-between pt-2">
          <button
            onClick={handleCreateEsentralPreview}
            disabled={esentralPreviewing || esentralCreating || !esentralFormData.kundeNr || !esentralFormData.navn}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {esentralPreviewing ? 'Laster...' : 'Forhåndsvis'}
          </button>
          <div className="flex space-x-2">
            <button
              onClick={handleCreateEsentralCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={esentralCreating}
            >
              Avbryt
            </button>
            <button
              onClick={handleCreateEsentralConfirm}
              disabled={esentralCreating || !esentralFormData.kundeNr || !esentralFormData.navn}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {esentralCreating ? 'Oppretter...' : 'Opprett eSentral Firma'}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    {/* eSentral Configuration View Modal */}
    <Modal
      isOpen={showConfigModal}
      onClose={handleCloseConfigModal}
      title="eSentral Konfigurasjon"
    >
      <div className="space-y-4">
        {loadingConfig ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Laster konfigurasjon...</span>
          </div>
        ) : viewingConfig ? (
          <>
            <ESentralConfigView config={viewingConfig} />
          </>
        ) : (
          <div className="text-center py-8 text-red-700">
            {configError || 'Kunne ikke laste konfigurasjon'}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            onClick={handleCloseConfigModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Lukk
          </button>
        </div>
      </div>
    </Modal>

    {/* Database Cleanup Modal */}
    <Modal
      isOpen={showCleanupModal}
      onClose={handleCloseCleanupModal}
      title={`Tøm database - ${cleanupTenant?.tenantName || ''}`}
    >
      <div className="space-y-4">
        {cleanupLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <span className="ml-2 text-gray-600">Analyserer database...</span>
          </div>
        ) : cleanupPreview ? (
          <>
            {/* Info om hva som vil skje */}
            <div className="bg-orange-50 border border-orange-200 rounded p-3">
              <p className="text-sm text-orange-800">
                <strong>Advarsel:</strong> Denne operasjonen vil slette alle transaksjonsdata
                for CompanyNo <strong>{cleanupPreview.companyNo}</strong> i databasen <strong>{cleanupPreview.databaseName}</strong>.
                Stamdata (artikler, lokasjoner, etc.) beholdes.
              </p>
            </div>

            {/* Sammendrag */}
            <div className="bg-gray-50 p-3 rounded">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Tabeller:</span>{' '}
                  <span className="font-medium">{cleanupPreview.totalTables}</span>
                </div>
                <div>
                  <span className="text-gray-500">Rader som slettes:</span>{' '}
                  <span className="font-medium text-red-600">{cleanupPreview.totalRowsToDelete.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Advarsler */}
            {cleanupPreview.warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <h4 className="text-sm font-medium text-yellow-800 mb-1">Bemerkninger:</h4>
                <ul className="text-xs text-yellow-700 space-y-0.5">
                  {cleanupPreview.warnings.map((warning, idx) => (
                    <li key={idx}>• {warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tabell-liste med radtelling */}
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="bg-gray-100 px-3 py-2">
                <h4 className="text-sm font-medium text-gray-900">Tabeller som tømmes</h4>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5 text-gray-600">Tabell</th>
                      <th className="text-left px-3 py-1.5 text-gray-600">Kategori</th>
                      <th className="text-right px-3 py-1.5 text-gray-600">Rader</th>
                      <th className="text-center px-3 py-1.5 text-gray-600">Spesielt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cleanupPreview.tables.map((table, idx) => (
                      <tr key={idx} className={table.rowCount > 0 ? 'bg-red-50' : ''}>
                        <td className="px-3 py-1.5 font-mono">{table.tableName}</td>
                        <td className="px-3 py-1.5 text-gray-500">{table.category || '-'}</td>
                        <td className="px-3 py-1.5 text-right font-medium">
                          {table.rowCount > 0 ? (
                            <span className="text-red-600">{table.rowCount.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {table.hasPreAction && (
                            <span className="text-blue-600" title={table.preActionDescription}>⚡</span>
                          )}
                          {table.requiresDisabledTriggers && (
                            <span className="text-yellow-600 ml-1" title="Triggere deaktiveres">🔒</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Resultat etter kjøring */}
            {cleanupResult && (
              <div className={`border rounded p-3 ${cleanupResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`text-sm font-medium mb-1 ${cleanupResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {cleanupResult.success ? '✓ Cleanup fullført' : '✗ Cleanup feilet'}
                </h4>
                <p className={`text-xs ${cleanupResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {cleanupResult.message}
                </p>
                {cleanupResult.duration && (
                  <p className="text-xs text-gray-500 mt-1">Varighet: {cleanupResult.duration}</p>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Kunne ikke analysere databasen
          </div>
        )}

        {/* Knapper */}
        <div className="flex justify-end space-x-2 pt-2 border-t">
          <button
            onClick={handleCloseCleanupModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {cleanupResult?.success ? 'Lukk' : 'Avbryt'}
          </button>
          {cleanupPreview && cleanupPreview.totalRowsToDelete > 0 && !cleanupResult?.success && (
            <button
              onClick={handleExecuteCleanup}
              disabled={cleanupExecuting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {cleanupExecuting ? 'Tømmer...' : `Tøm database (${cleanupPreview.totalRowsToDelete.toLocaleString()} rader)`}
            </button>
          )}
        </div>
      </div>
    </Modal>

    {/* CompanyNo Update Modal */}
    <Modal
      isOpen={showCompanyNoModal}
      onClose={handleCloseCompanyNoModal}
      title={`Oppdater CompanyNo - ${companyNoTenant?.tenantName || ''}`}
    >
      <div className="space-y-4">
        {companyNoLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-2 text-gray-600">Henter kolonner...</span>
          </div>
        ) : companyNoColumns.length > 0 ? (
          <>
            {/* Info */}
            <div className="bg-purple-50 border border-purple-200 rounded p-3">
              <p className="text-sm text-purple-800">
                <strong>Info:</strong> Denne operasjonen endrer CompanyNo fra valgt firma til ny verdi i alle
                relevante tabeller i tenant-databasen <strong>{companyNoTenant?.databaseName}</strong>.
                Rader for andre firma røres ikke, og alt rulles tilbake ved feil.
              </p>
            </div>

            {/* Gammel CompanyNo (fra Company-tabellen) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fra CompanyNo
              </label>
              {companyNoCompanies.length > 1 ? (
                <select
                  value={oldCompanyNo}
                  onChange={(e) => setOldCompanyNo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Velg firma som skal endres</option>
                  {companyNoCompanies.map(c => (
                    <option key={c.companyNo} value={c.companyNo}>
                      {c.companyNo}{c.companyName ? ` – ${c.companyName}` : ''}
                    </option>
                  ))}
                </select>
              ) : companyNoCompanies.length === 1 ? (
                <p className="text-sm text-gray-900">
                  <strong>{companyNoCompanies[0].companyNo}</strong>
                  {companyNoCompanies[0].companyName ? ` – ${companyNoCompanies[0].companyName}` : ''}
                </p>
              ) : (
                <p className="text-sm text-red-700">Company-tabellen er tom – kan ikke oppdatere CompanyNo</p>
              )}
            </div>

            {/* Ny CompanyNo input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ny CompanyNo
              </label>
              <input
                type="text"
                value={newCompanyNo}
                onChange={(e) => setNewCompanyNo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Skriv inn ny CompanyNo"
              />
              {companyNoTenant?.eSentralFirmaNr && (
                <p className="text-xs text-gray-500 mt-1">
                  eSentral FirmaNr: <strong>{companyNoTenant.eSentralFirmaNr}</strong>
                </p>
              )}
            </div>

            {/* Tabell-liste som vil oppdateres */}
            <div className="border border-gray-200 rounded overflow-hidden">
              <div className="bg-gray-100 px-3 py-2">
                <h4 className="text-sm font-medium text-gray-900">
                  Tabeller som oppdateres ({companyNoColumns.length})
                </h4>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5 text-gray-600">Tabell</th>
                      <th className="text-left px-3 py-1.5 text-gray-600">Kolonne</th>
                      <th className="text-left px-3 py-1.5 text-gray-600">Nåværende verdi</th>
                      <th className="text-right px-3 py-1.5 text-gray-600">Rader</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {companyNoColumns.map((col, idx) => (
                      <tr key={idx} className={col.rowCount > 0 ? 'bg-purple-50' : ''}>
                        <td className="px-3 py-1.5 font-mono">{col.tableName}</td>
                        <td className="px-3 py-1.5 text-gray-600">{col.columnName}</td>
                        <td className="px-3 py-1.5">
                          {col.currentValue ? (
                            <code className="bg-gray-100 px-1 rounded">{col.currentValue}</code>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-medium">
                          {col.rowCount > 0 ? (
                            <span className="text-purple-600">{col.rowCount.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Resultat etter kjøring */}
            {companyNoResult && (
              <div className={`border rounded p-3 ${companyNoResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`text-sm font-medium mb-1 ${companyNoResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {companyNoResult.success ? '✓ Oppdatering fullført' : '✗ Oppdatering feilet'}
                </h4>
                <p className={`text-xs ${companyNoResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {companyNoResult.message}
                </p>
                {companyNoResult.success && companyNoResult.totalRowsUpdated > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    Totalt {companyNoResult.totalRowsUpdated.toLocaleString()} rader oppdatert
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Ingen CompanyNo/CustomerNo-kolonner funnet i databasen
          </div>
        )}

        {/* Knapper */}
        <div className="flex justify-end space-x-2 pt-2 border-t">
          <button
            onClick={handleCloseCompanyNoModal}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {companyNoResult?.success ? 'Lukk' : 'Avbryt'}
          </button>
          {companyNoColumns.length > 0 && !companyNoResult?.success && (
            <button
              onClick={handleExecuteCompanyNoUpdate}
              disabled={companyNoUpdating || !newCompanyNo.trim() || !oldCompanyNo}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {companyNoUpdating ? 'Oppdaterer...' : 'Oppdater CompanyNo'}
            </button>
          )}
        </div>
      </div>
    </Modal>
    </>
  );
};

export default TenantList;